import { useState } from 'react';
import { PageSettings } from '@/types/editor';
import { supabase } from '@/integrations/backend/client';
import { trackFunnelStep } from '@/lib/funnelTracking';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Check, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { pickByLanguage } from '@/lib/language';

interface EmailCollectionFormProps {
  settings: PageSettings;
  userId: string;
  showContainer?: boolean;
  showIntro?: boolean;
  className?: string;
  stylePreset?: 'auto' | 'soft' | 'solid' | 'outline';
}

export function EmailCollectionForm({
  settings,
  userId,
  showContainer = true,
  showIntro = true,
  className,
  stylePreset = 'auto',
}: EmailCollectionFormProps) {
  const { language } = useLanguage();
  const t = (pt: string, en: string) => pickByLanguage(language, { pt, en });
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const resolvedStylePreset = stylePreset === 'auto'
    ? (settings.button_fill ?? 'filled') === 'outline'
      ? 'outline'
      : (settings.button_shape ?? 'rounded') === 'square'
        ? 'solid'
        : 'soft'
    : stylePreset;

  const containerClass = cn(
    'rounded-2xl border p-5 sm:p-6 shadow-lg',
    resolvedStylePreset === 'solid' && 'bg-card border-border/80',
    resolvedStylePreset === 'soft' && 'bg-card/95 border-border/70 backdrop-blur-sm',
    resolvedStylePreset === 'outline' && 'bg-transparent border-border',
  );

  const fieldClass = cn(
    'h-11 rounded-xl focus-visible:ring-primary/40',
    resolvedStylePreset === 'solid' && 'border-border bg-background',
    resolvedStylePreset === 'soft' && 'border-border/70 bg-background/80',
    resolvedStylePreset === 'outline' && 'border-border bg-background/70',
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error(t('Digite seu email', 'Please enter your email'));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t('Digite um email válido', 'Please enter a valid email'));
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .rpc('subscribe_email', {
          _user_id: userId,
          _email: email.toLowerCase().trim(),
          _name: name.trim() || null,
          _source: 'bio_page',
        });

      if (error) {
        // Check if it's a duplicate or validation error
        if (error.message?.includes('Invalid email')) {
          toast.error(t('Digite um email válido', 'Please enter a valid email'));
        } else if (error.message?.includes('too long')) {
          toast.error(t('Dados muito longos', 'Input too long'));
        } else {
          throw error;
        }
        return;
      }

      setSubscribed(true);
      toast.success(t('Inscrição realizada com sucesso!', 'Successfully subscribed!'));
      trackFunnelStep(userId, 'lead', 'lead_form_submit').catch(() => {});
      setEmail('');
      setName('');
      
      // Reset after 5 seconds
      setTimeout(() => setSubscribed(false), 5000);
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error(t('Não foi possível concluir a inscrição. Tente novamente.', 'Failed to subscribe. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (!settings.email_collection_enabled) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn('w-full max-w-md mx-auto', className)}
    >
      <div className={showContainer ? containerClass : ''}>
        <AnimatePresence mode="wait">
          {subscribed ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-center py-4"
            >
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                <Check className="h-8 w-8 text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {t('Inscrição confirmada!', "You're subscribed!")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('Obrigado por se inscrever. Vamos manter você atualizado.', "Thank you for subscribing. We'll keep you updated.")}
              </p>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onSubmit={handleSubmit}
              className="space-y-3.5"
            >
              {showIntro && (
                <div className="text-center mb-3">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 mb-2">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    {settings.email_collection_title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {settings.email_collection_description}
                  </p>
                </div>
              )}

              <div className="space-y-2.5">
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground/60" />
                  <Input
                    type="text"
                    placeholder={t('Seu nome (opcional)', 'Your name (optional)')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={loading}
                    className={cn(fieldClass, "pl-11 h-12")}
                  />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground/60" />
                  <Input
                    type="email"
                    placeholder={t('Seu endereço de email', 'Your email address')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required
                    className={cn(fieldClass, "pl-11 h-12")}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-xl transition-colors focus-visible:ring-primary/40"
                disabled={loading || !email}
                style={{
                  backgroundColor: settings.button_color,
                  color: settings.button_text_color,
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin motion-reduce:animate-none" />
                    {t('Inscrevendo...', 'Subscribing...')}
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    {settings.email_collection_button_text}
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground/90">
                {t('Respeitamos sua privacidade. Cancele a inscrição quando quiser.', 'We respect your privacy. Unsubscribe at any time.')}
              </p>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
